import {
  invalidateSearchQueries,
  isCanonicalSearchQuery,
} from "@/lib/data/react-query/search/invalidation";
import {
  createConnectionV4,
  deleteConnectionV4,
} from "@/lib/data/requests/connections/connection";
import { queryKeys } from "@/lib/data/static/query-keys";
import type { APIError, MatterIncidentReport } from "@/lib/types";
import { toastAPIErrorMessage } from "@/lib/utils/axiosUtils";
import {
  createIncidentReportContactConnectionInput,
  getIncidentReportContactConnections,
  type SupportedIncidentReportContactRole,
} from "@/lib/utils/connections/incident-report-contact-connections";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";

export type IncidentReportContactIdsByRole = Partial<
  Record<SupportedIncidentReportContactRole, string[]>
>;

type SyncIncidentReportConnectionsInput = {
  currentIncidentReport?: MatterIncidentReport;
  desiredContactIdsByRole: IncidentReportContactIdsByRole;
  incidentReportId: string;
  matterId: string;
};

export const syncIncidentReportContactConnections = async ({
  currentIncidentReport,
  desiredContactIdsByRole,
  incidentReportId,
}: SyncIncidentReportConnectionsInput) => {
  if (currentIncidentReport?.connectionsNextCursor)
    throw new Error("Load all owner connections before changing roles.");
  await Promise.all(
    Object.entries(desiredContactIdsByRole).map(async ([role, contactIds]) => {
      const current = getIncidentReportContactConnections(
        currentIncidentReport,
        role as SupportedIncidentReportContactRole,
      );
      const desired = [...new Set(contactIds.filter(Boolean))];
      const desiredSet = new Set(desired);

      await Promise.all([
        ...current
          .filter((connection) => !desiredSet.has(connection.targetObjectId))
          .map((connection) => deleteConnectionV4(connection)),
        ...desired
          .filter(
            (contactId) =>
              !current.some(
                (connection) => connection.targetObjectId === contactId,
              ),
          )
          .map((contactId) =>
            createConnectionV4(
              createIncidentReportContactConnectionInput(
                incidentReportId,
                contactId,
                role as SupportedIncidentReportContactRole,
              ),
            ),
          ),
      ]);
    }),
  );
};

export const useSyncIncidentReportContactConnections = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: syncIncidentReportContactConnections,
    onError: (error: AxiosError<APIError>) => {
      toastAPIErrorMessage(error, "Error updating incident report connections");
    },
    onSettled: async (_data, _error, variables) => {
      await invalidateSearchQueries(queryClient);
      await queryClient.invalidateQueries({
        predicate: (query) => !isCanonicalSearchQuery(query),
        queryKey: [queryKeys.matters, variables.matterId],
      });
    },
  });
};
